import { useState } from 'react';
import { CheckCircle2, FileCode2, Printer } from 'lucide-react';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { colorDeLaSituacion } from './estadosDeFactura';
import { useComprobante } from './useComprobante';
import type { Autorizada } from './tiposDelProcesoSri';

export function TablaDeAutorizadas({
  filas,
  corriendo,
}: {
  filas: Autorizada[];
  corriendo: boolean;
}) {
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);
  const comprobante = useComprobante();

  const totalPaginas = Math.max(1, Math.ceil(filas.length / tamanio));
  const actual = Math.min(pagina, totalPaginas - 1);

  const datos = {
    contenido: filas.slice(actual * tamanio, actual * tamanio + tamanio),
    totalElementos: filas.length,
    pagina: actual,
    tamanio,
    totalPaginas,
  };

  const columnas: Columna<Autorizada>[] = [
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      contenido: (f) => (
        <span className="block">
          <span className="block truncate font-medium text-armazon-700">{f.cliente}</span>
          <span className="block font-mono text-[10px] text-armazon-400">{f.identificacion}</span>
        </span>
      ),
    },
    { clave: 'periodo', encabezado: 'Período', contenido: (f) => f.periodo },
    {
      clave: 'numero',
      encabezado: 'Nº factura',
      contenido: (f) => <span className="font-mono text-xs">{f.numero}</span>,
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => <span className="importe">${f.total.toFixed(2)}</span>,
    },
    {
      clave: 'situacion',
      encabezado: 'Estado SRI',
      alinear: 'centro',
      contenido: (f) => (
        <span
          className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase ${colorDeLaSituacion(f.situacion)}`}
        >
          <CheckCircle2 className="size-3.5" aria-hidden />
          {f.situacion}
        </span>
      ),
    },
    {
      clave: 'autorizacion',
      encabezado: 'Nº autorización',
      contenido: (f) => (
        <span
          className="block max-w-[220px] truncate font-mono text-[10px] text-armazon-500"
          title={f.numeroAutorizacion ?? ''}
        >
          {f.numeroAutorizacion ?? '—'}
        </span>
      ),
    },
    {
      clave: 'fecha',
      encabezado: 'Fecha autorización',
      contenido: (f) => (
        <span className="whitespace-nowrap text-[11px] text-armazon-500">
          {f.autorizadaEn
            ? new Date(f.autorizadaEn).toLocaleString('es-EC', {
                dateStyle: 'short',
                timeStyle: 'medium',
              })
            : '—'}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'derecha',
      contenido: (f) => (
        <span className="flex justify-end gap-1">
          <BotonDeFila
            icono={Printer}
            titulo="Abrir el RIDE"
            alPulsar={() => void comprobante.abrirRide(f.id)}
            ocupado={comprobante.ocupado === f.id}
          />
          <BotonDeFila
            icono={FileCode2}
            titulo="Descargar el XML autorizado"
            alPulsar={() => void comprobante.descargarXml(f.id, f.numero)}
            ocupado={comprobante.ocupado === f.id}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <Tabla
        datos={datos}
        cargando={false}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(nuevo) => {
          setTamanio(nuevo);
          setPagina(0);
        }}
        iconoVacio={CheckCircle2}
        mensajeVacio="Todavía no hay ninguna autorizada"
        entidad="AUTORIZADAS"
        herramientas={
          corriendo ? (
            <span className="flex items-center gap-1.5 rounded-full bg-marca-50 px-2 py-1 text-[9px] font-bold uppercase text-marca-700">
              <span className="size-1.5 animate-pulse rounded-full bg-marca-500" />
              En vivo
            </span>
          ) : undefined
        }
      />

      <VistaPreviaDeImpresion
        abierto={comprobante.ride !== null || comprobante.ocupado !== null}
        alCerrar={comprobante.cerrar}
        titulo="RIDE"
        subtitulo="Representación impresa del comprobante electrónico"
        fichero={comprobante.ride}
        cargando={comprobante.ocupado !== null}
        formato="hoja"
        textoDeCarga="Preparando el RIDE…"
      />
    </>
  );
}
